import React, { useState } from 'react';
import { Upload, Button, Typography, message, Progress, Tag } from 'antd';
import { InboxOutlined, DeleteOutlined, FileTextOutlined, CheckCircleOutlined } from '@ant-design/icons';

const { Dragger } = Upload;
const { Title, Text } = Typography;

const CustomerCSVUpload = ({ onCustomersLoaded, uploadedCustomers, onClearCustomers }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Expected CSV columns
  const expectedColumns = ['name', 'email', 'phone', 'address', 'city', 'state', 'zip'];
  
  const handleFileUpload = async (file) => {
    setUploading(true);
    setUploadProgress(0);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 100);

      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        throw new Error('CSV file must contain at least a header row and one data row');
      }

      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      
      // Validate headers
      const missingColumns = expectedColumns.filter(col => !headers.includes(col));
      if (missingColumns.length > 0) {
        throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
      }

      // Parse data rows
      const customers = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        if (values.length >= expectedColumns.length) {
          const customer = {};
          headers.forEach((header, index) => {
            customer[header] = values[index] || '';
          });
          
          // Validate required fields
          if (customer.name && (customer.email || customer.phone)) {
            customers.push({
              ...customer,
              id: `csv_${i}`, // Temporary ID for display
              source: 'CSV Upload',
              uploadTime: new Date().toISOString()
            });
          }
        }
      }

      if (customers.length === 0) {
        throw new Error('No valid customer data found in CSV');
      }

      clearInterval(progressInterval);
      setUploadProgress(100);
      
      // Update parent component with customer data
      onCustomersLoaded(customers);
      
      message.success(`Successfully loaded ${customers.length} customers from CSV`);
      
    } catch (error) {
      console.error('CSV parsing error:', error);
      message.error(`Failed to parse CSV: ${error.message}`);
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  const handleClear = () => {
    onClearCustomers();
    message.info('Customer data cleared');
  };

  const uploadProps = {
    name: 'file',
    multiple: false,
    accept: '.csv',
    beforeUpload: (file) => {
      if (!file.name.endsWith('.csv')) {
        message.error('Please upload a CSV file');
        return false;
      }
      
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        message.error('File size must be less than 5MB');
        return false;
      }

      handleFileUpload(file);
      return false; // Prevent automatic upload
    },
    showUploadList: false,
  };

      return (
    <div 
      style={{ 
        backgroundColor: '#fff',
        borderRadius: '12px',
        padding: '20px',
        border: '1px solid #e8e8e8',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
        height: '100%'
      }}
    >
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <FileTextOutlined style={{ fontSize: '18px', color: 'var(--ink)' }} />
            <Title level={5} style={{ margin: 0, color: '#262626', fontWeight: 600, fontSize: '16px' }}>
              Customer Data Upload
            </Title>
          </div>
          <Text style={{ color: '#8c8c8c', fontSize: '13px', lineHeight: '1.4' }}>
            Upload CSV with customer info for SMS/email campaigns. Data processed in memory only.
          </Text>
        </div>

        {!uploadedCustomers || uploadedCustomers.length === 0 ? (
          <>
            <Dragger 
              {...uploadProps} 
              style={{ 
                backgroundColor: '#fafafa',
                borderColor: '#d9d9d9',
                borderRadius: '8px',
                borderStyle: 'dashed',
                padding: '16px'
              }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined style={{ fontSize: '40px', color: 'var(--ink)' }} />
              </p>
              <p className="ant-upload-text" style={{ color: '#262626', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                Click or drag CSV file here
              </p>
              <p className="ant-upload-hint" style={{ color: '#8c8c8c', fontSize: '12px', margin: 0 }}>
                Required: name, email, phone, address, city, state, zip
              </p>
            </Dragger>

            {uploading && (
              <div style={{ marginTop: '12px' }}>
                <Progress percent={uploadProgress} status="active" strokeColor="var(--ink)" size="small" />
                <Text style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '4px', display: 'block' }}>
                  Processing...
                </Text>
              </div>
            )}
          </>
        ) : (
          <div style={{
            padding: '14px 16px',
            backgroundColor: '#f6ffed',
            border: '1px solid #b7eb8f',
            borderRadius: '8px',
            textAlign: 'center',
          }}>
            <CheckCircleOutlined style={{ color: 'var(--sage)', fontSize: '24px', display: 'block', marginBottom: '6px' }} />
            <Text strong style={{ color: '#262626', fontSize: '14px', display: 'block', marginBottom: '4px' }}>
              {uploadedCustomers.length} customers loaded
            </Text>
            <Tag color="success" style={{ fontSize: '11px', marginBottom: '10px' }}>Ready</Tag>
            <div>
              <Button 
                danger 
                icon={<DeleteOutlined />} 
                onClick={handleClear}
                size="small"
                style={{ fontSize: '12px' }}
              >
                Clear
              </Button>
            </div>
          </div>
        )}
      </div>
  );
};

export default CustomerCSVUpload;
